import { useEffect, useRef } from 'react'
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import Products from './components/pages/Products'
import Cart from './components/pages/Cart'
import Profile from './components/pages/Profile'
import ProductForm from './components/pages/ProductForm'
import ProductEdit from './components/pages/ProductEdit'
import ProductDetail from './components/pages/ProductDetail'
import PersonalStore from './components/pages/PersonalStore'
import SellerProfile from './components/pages/SellerProfile'
import Settings from './components/pages/Settings'
import Favorites from './components/pages/Favorites'
import StaticPage from './components/pages/StaticPage'
import Admin from './components/pages/Admin'
import ErrorBoundary from './components/ErrorBoundary'
import PageTransition from './components/ui/PageTransition'
import BottomNav from './components/layout/BottomNav'
import TermsConsent from './components/TermsConsent'
import Onboarding from './components/Onboarding'
import { TelegramClosingConfirmation } from './components/telegram/TelegramClosingConfirmation'
import { CartProvider } from './contexts/CartContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { useTelegramApp } from './hooks/useTelegramLifecycle'
import { useTelegramBackButton } from './hooks/useTelegramBackButton'
import '@telegram-apps/telegram-ui/dist/styles.css'

function AppContent() {
  const location = useLocation()
  const navigate = useNavigate()

  // Initialize Telegram Mini App with fullscreen mode
  const telegramApp = useTelegramApp({
    autoReady: true,
    autoExpand: true,
    requestFullscreen: true,
    disableVerticalSwipes: true,
    onReady: () => console.log('📱 Telegram Mini App is ready!'),
  })

  // Handle deep link from startapp parameter - process only once
  const deepLinkProcessed = useRef(false)

  useEffect(() => {
    // Only process deep link once on initial load
    if (deepLinkProcessed.current) return

    const tg = window.Telegram?.WebApp
    if (!tg) return

    // Get startapp parameter from Telegram WebApp
    const startParam = tg.initDataUnsafe?.start_param

    // Also check URL for tgWebAppStartParam
    const urlParams = new URLSearchParams(window.location.search)
    const urlStartParam = urlParams.get('tgWebAppStartParam')

    const param = startParam || urlStartParam

    if (param) {
      // Mark as processed to prevent re-navigation
      deepLinkProcessed.current = true

      if (param.startsWith('product_')) {
        // Extract product ID from parameter (convert underscores back to hyphens)
        const productId = param.replace('product_', '').replace(/_/g, '-')
        console.log('📦 Deep link detected, navigating to product:', productId)
        navigate(`/product/${productId}`)
      } else if (param.startsWith('store_')) {
        // Handle store deep links
        const userId = param.replace('store_', '').replace(/_/g, '-')
        console.log('🏪 Deep link detected, navigating to store:', userId)
        navigate(`/store/${userId}`)
      }
    }
  }, [navigate])

  // Determine if we're on a main page (home/products)
  const isMainPage = location.pathname === '/' ||
                     location.pathname === '/products'

  // Determine if we're on the home page
  const isHomePage = location.pathname === '/' || location.pathname === '/products'

  // Back button click handler - simple native behavior
  const handleBackButtonClick = () => {
    if (isHomePage) {
      // On home page, close the app
      console.log('🏠 Home page - closing app')
      telegramApp.close()
      return
    }

    // On any other page, go back to previous page
    console.log('⬅️ Going back to previous page')
    navigate(-1)
  }

  // Setup back button - only show on non-main pages
  useTelegramBackButton({
    onClick: handleBackButtonClick,
    isVisible: !isMainPage // Hide on main tabbed pages
  })

  // Log location changes for debugging
  useEffect(() => {
    console.log('🔄 Location changed to:', location.pathname)
    console.log('🔘 Back button visible:', !isMainPage)
  }, [location.pathname, isMainPage])

  return (
    <ErrorBoundary>
      <TelegramClosingConfirmation
        enabled={false}
        message="Are you sure you want to leave the shop?"
      />
      {/* One-time Terms/Privacy acceptance gate */}
      <TermsConsent />
      {/* One-time onboarding for first-time visitors */}
      <Onboarding />
      {/* Routes handle their own layout via PageLayout */}
      <PageTransition>
        <Routes>
        {/* Main pages with bottom navigation */}
        <Route path="/" element={<Products />} />
        <Route path="/products" element={<Products />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/profile" element={<Profile />} />
        {/* Secondary pages */}
        <Route path="/product/new" element={<ProductForm />} />
        <Route path="/product/:id/edit" element={<ProductEdit />} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/store/:userId" element={<PersonalStore />} />
        <Route path="/seller/:userId" element={<SellerProfile />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/favorites" element={<Favorites />} />
        <Route path="/page/:slug" element={<StaticPage />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
      </PageTransition>

      {/* Bottom navigation lives OUTSIDE PageTransition: its wrapper sets a
          transform, which would otherwise become the containing block for this
          fixed bar and unpin it from the viewport. It self-hides on deep pages. */}
      <BottomNav />
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </ThemeProvider>
    </BrowserRouter>
  )
}